import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "sp_admin_token";

export const tokenStorage = {
  get: () => (typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null),
  set: (t) => localStorage.setItem(TOKEN_KEY, t),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

const client = axios.create({
  baseURL: API,
  timeout: 20000,
});

client.interceptors.request.use((config) => {
  const t = tokenStorage.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path.startsWith("/admin") && path !== "/admin/login") {
        tokenStorage.clear();
        window.location.assign("/admin/login");
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  // Public
  siteSettings: () => client.get("/site-settings").then((r) => r.data),
  categories: () => client.get("/categories").then((r) => r.data),
  category: (slug) => client.get(`/categories/${slug}`).then((r) => r.data),
  articles: (params = {}) => client.get("/articles", { params }).then((r) => r.data),
  mostRead: (limit = 6) => client.get("/articles/most-read", { params: { limit } }).then((r) => r.data),
  article: (slug) => client.get(`/articles/${slug}`).then((r) => r.data),
  trackArticleView: (slug, device) =>
    client.post(`/articles/${slug}/view`, { device }).then((r) => r.data).catch(() => null),
  related: (slug, limit = 4) => client.get(`/articles/${slug}/related`, { params: { limit } }).then((r) => r.data),
  search: (q, limit = 30) => client.get("/search", { params: { q, limit } }).then((r) => r.data),
  contact: (payload) => client.post("/contact", payload).then((r) => r.data),
  homepageLayout: (device) => client.get("/homepage/layout", { params: device ? { device } : {} }).then((r) => r.data),

  // SEO
  articleSEO: (slug) => client.get(`/articles/${slug}/seo`).then((r) => r.data),
  categorySEO: (slug) => client.get(`/categories/${slug}/seo`).then((r) => r.data),
  seoSite: () => client.get("/seo/site").then((r) => r.data),

  // Auth
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    client.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  passwordResetRequest: (email) =>
    client.post("/auth/password-reset-request", { email }).then((r) => r.data),

  // RBAC
  adminListRoles: () => client.get("/admin/roles").then((r) => r.data),
  adminListUsers: (params = {}) => client.get("/admin/users", { params }).then((r) => r.data),
  adminGetUser: (id) => client.get(`/admin/users/${id}`).then((r) => r.data),
  adminCreateUser: (payload) => client.post("/admin/users", payload).then((r) => r.data),
  adminUpdateUser: (id, payload) => client.put(`/admin/users/${id}`, payload).then((r) => r.data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  adminResetUserPassword: (id, new_password) =>
    client.post(`/admin/users/${id}/reset-password`, { new_password: new_password || null }).then((r) => r.data),
  adminCloseUserSessions: (id) =>
    client.post(`/admin/users/${id}/close-sessions`).then((r) => r.data),
  adminListResetRequests: (params = {}) =>
    client.get("/admin/password-reset-requests", { params }).then((r) => r.data),

  // Audit
  adminListAudit: (params = {}) => client.get("/admin/audit-logs", { params }).then((r) => r.data),

  // Admin
  adminStats: () => client.get("/admin/dashboard/stats").then((r) => r.data),
  adminListArticles: (params = {}) => client.get("/admin/articles", { params }).then((r) => r.data),
  adminGetArticle: (id) => client.get(`/admin/articles/${id}`).then((r) => r.data),
  adminCreateArticle: (payload) => client.post("/admin/articles", payload).then((r) => r.data),
  adminUpdateArticle: (id, payload) => client.put(`/admin/articles/${id}`, payload).then((r) => r.data),
  adminArticleAction: (id, body) => client.post(`/admin/articles/${id}/action`, body).then((r) => r.data),
  adminDuplicateArticle: (id) => client.post(`/admin/articles/${id}/duplicate`).then((r) => r.data),
  adminDeleteArticle: (id) => client.delete(`/admin/articles/${id}`).then((r) => r.data),

  adminListCategories: () => client.get("/admin/categories").then((r) => r.data),
  adminCreateCategory: (payload) => client.post("/admin/categories", payload).then((r) => r.data),
  adminUpdateCategory: (id, payload) => client.put(`/admin/categories/${id}`, payload).then((r) => r.data),
  adminDeleteCategory: (id, force = false) => client.delete(`/admin/categories/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),
  adminReorderCategories: (ids) => client.post("/admin/categories/reorder", { ids }).then((r) => r.data),

  adminListAuthors: () => client.get("/admin/authors").then((r) => r.data),
  adminCreateAuthor: (payload) => client.post("/admin/authors", payload).then((r) => r.data),

  adminUpdateSettings: (payload) => client.put("/admin/settings", payload).then((r) => r.data),
  adminContactMessages: () => client.get("/admin/contact-messages").then((r) => r.data),

  // Homepage manager
  adminListBlocks: () => client.get("/admin/homepage/blocks").then((r) => r.data),
  adminGetBlock: (id) => client.get(`/admin/homepage/blocks/${id}`).then((r) => r.data),
  adminGetBlockResolved: (id) => client.get(`/admin/homepage/blocks/${id}/resolved`).then((r) => r.data),
  adminCreateBlock: (payload) => client.post("/admin/homepage/blocks", payload).then((r) => r.data),
  adminUpdateBlock: (id, payload) => client.put(`/admin/homepage/blocks/${id}`, payload).then((r) => r.data),
  adminDeleteBlock: (id) => client.delete(`/admin/homepage/blocks/${id}`).then((r) => r.data),
  adminReorderBlocks: (ids) => client.post("/admin/homepage/reorder", { ids }).then((r) => r.data),
  adminSetBlockArticles: (id, ids) => client.post(`/admin/homepage/blocks/${id}/articles`, { ids }).then((r) => r.data),

  // Ads
  serveAd: (slot, device, category, articleId) =>
    client.get("/ads/serve", { params: { slot, device, category, article_id: articleId } }).then((r) => r.data),
  serveAdAll: (slot, device, category, articleId) =>
    client.get("/ads/serve", { params: { slot, device, category, article_id: articleId, all: true } }).then((r) => r.data),
  logAdEvent: (creative_id, event_type, device) =>
    client.post("/ads/event", { creative_id, event_type, device }).then((r) => r.data).catch(() => null),

  adminListCampaigns: () => client.get("/admin/ads/campaigns").then((r) => r.data),
  adminCreateCampaign: (payload) => client.post("/admin/ads/campaigns", payload).then((r) => r.data),
  adminUpdateCampaign: (id, payload) => client.put(`/admin/ads/campaigns/${id}`, payload).then((r) => r.data),
  adminDeleteCampaign: (id, force = false) => client.delete(`/admin/ads/campaigns/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),

  adminListAdSlots: () => client.get("/admin/ads/slots").then((r) => r.data),
  adminCreateAdSlot: (payload) => client.post("/admin/ads/slots", payload).then((r) => r.data),
  adminUpdateAdSlot: (id, payload) => client.put(`/admin/ads/slots/${id}`, payload).then((r) => r.data),
  adminDeleteAdSlot: (id, force = false) => client.delete(`/admin/ads/slots/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),

  adminListCreatives: (params = {}) => client.get("/admin/ads/creatives", { params }).then((r) => r.data),
  adminGetCreative: (id) => client.get(`/admin/ads/creatives/${id}`).then((r) => r.data),
  adminCreateCreative: (payload) => client.post("/admin/ads/creatives", payload).then((r) => r.data),
  adminUpdateCreative: (id, payload) => client.put(`/admin/ads/creatives/${id}`, payload).then((r) => r.data),
  adminCreativeAction: (id, action) => client.post(`/admin/ads/creatives/${id}/action`, { action }).then((r) => r.data),
  adminDeleteCreative: (id) => client.delete(`/admin/ads/creatives/${id}`).then((r) => r.data),

  adminAdsStats: (params = {}) => client.get("/admin/ads/stats", { params }).then((r) => r.data),
  adminDiagnoseCreative: (id, params = {}) => client.get(`/admin/ads/creatives/${id}/diagnose`, { params }).then((r) => r.data),
  adminDiagnoseAllAds: () => client.get("/admin/ads/diagnose-all").then((r) => r.data),

  // Editorial statistics
  adminStatsFilters: () => client.get("/admin/stats/filters").then((r) => r.data),
  adminEditorialStats: (params = {}) => client.get("/admin/stats/editorial", { params }).then((r) => r.data),
  adminEditorialStatsCsv: (params = {}) =>
    client
      .get("/admin/stats/editorial/export.csv", { params, responseType: "blob" })
      .then((r) => r.data),

  // AI editorial assistant (Claude Sonnet 4.5 via Emergent LLM Key)
  adminAiAssist: (payload) => client.post("/admin/ai/assist", payload, { timeout: 120000 }).then((r) => r.data),

  // System Health Check (admin dashboard)
  adminHealth: () => client.get("/admin/health", { timeout: 20000 }).then((r) => r.data),
  adminHealthEnableAutomation: () => client.post("/admin/health/enable-automation").then((r) => r.data),

  // Editorial engine (Etapa 1 — modelo + scoring, no publica)
  adminEditorialConfigGet: () => client.get("/editorial/config").then((r) => r.data),
  adminEditorialConfigPut: (patch) => client.put("/editorial/config", patch).then((r) => r.data),
  adminGetPublicationSlots: () => client.get("/admin/automation/publication-slots").then((r) => r.data),
  adminPatchPublicationSlots: (slots) => client.patch("/admin/automation/publication-slots", { slots }).then((r) => r.data),
  publicNextRun: () => client.get("/editorial/next-run").then((r) => r.data),
  getWeather: () => client.get("/weather").then((r) => r.data),
  adminGetWeatherWidget: () => client.get("/admin/weather-widget").then((r) => r.data),
  adminPatchWeatherWidget: (fields) => client.patch("/admin/weather-widget", { fields }).then((r) => r.data),
  adminEditorialLocations: () => client.get("/editorial/locations").then((r) => r.data),
  adminEditorialSources: () => client.get("/editorial/sources").then((r) => r.data),
  adminEditorialSourceTrust: (id, patch) => client.patch(`/editorial/sources/${id}/trust`, patch).then((r) => r.data),
  adminEditorialCandidates: (params = {}) => client.get("/editorial/candidates", { params }).then((r) => r.data),
  adminEditorialCandidate: (id) => client.get(`/editorial/candidates/${id}`).then((r) => r.data),
  adminEditorialEvaluate: (candidate_id) => client.post("/editorial/evaluate", { candidate_id }, { timeout: 30000 }).then((r) => r.data),
  adminEditorialPublishNow: (candidate_id, opts = {}) =>
    client.post(`/editorial/candidates/${candidate_id}/publish`, opts || {}, { timeout: 60000 }).then((r) => r.data),
  adminEditorialChannels: (id, patch) => client.patch(`/editorial/candidates/${id}/channels`, patch).then((r) => r.data),
  adminEditorialDecisionLog: (params = {}) => client.get("/editorial/decision-log", { params }).then((r) => r.data),
  adminEditorialEvents: (params = {}) => client.get("/editorial/events", { params }).then((r) => r.data),
  adminEditorialSeedCases: () => client.post("/editorial/dev/seed-cases", {}).then((r) => r.data),
  adminEditorialSeedExtended: () => client.post("/editorial/dev/seed-cases-extended", {}).then((r) => r.data),
  adminEditorialSimulate: (payload) => client.post("/editorial/simulate", payload, { timeout: 180000 }).then((r) => r.data),
  adminEditorialConfigVersions: () => client.get("/editorial/config/versions").then((r) => r.data),
  adminEditorialPanel: () => client.get("/editorial/panel").then((r) => r.data),
  adminEditorialNextRun: () => client.get("/editorial/next-run").then((r) => r.data),
  adminEditorialAutomationModeGet: () => client.get("/editorial/automation-mode").then((r) => r.data),
  adminEditorialAutomationModeSet: (mode) => client.put("/editorial/automation-mode", { automation_mode: mode }).then((r) => r.data),
  adminEditorialRuns: (params = {}) => client.get("/editorial/runs", { params }).then((r) => r.data),
  adminEditorialRunTrigger: (payload) => client.post("/editorial/runs/trigger", payload, { timeout: 180000 }).then((r) => r.data),
  adminEditorialSweepUnresolved: () => client.post("/editorial/unresolved/expire", {}).then((r) => r.data),
  adminEditorialEventDetail: (id) => client.get(`/editorial/events/${id}`).then((r) => r.data),
  adminEditorialMergeEvents: (payload) => client.post("/editorial/events/merge", payload).then((r) => r.data),
  adminEditorialDistributionConfig: () => client.get("/editorial/distribution/config").then((r) => r.data),
  adminEditorialDistributionConfigPut: (patch) => client.put("/editorial/distribution/config", patch).then((r) => r.data),
  adminEditorialDistributionStatus: () => client.get("/editorial/distribution/status").then((r) => r.data),
  adminEditorialDistributionQueue: (params = {}) => client.get("/editorial/distribution/queue", { params }).then((r) => r.data),
  adminEditorialDistributionProcess: (limit = 20) => client.post("/editorial/distribution/process-queue", null, { params: { limit } }).then((r) => r.data),
  adminEditorialDistributionRetry: (publicationId) => client.post(`/editorial/distribution/retry/${publicationId}`).then((r) => r.data),
  adminEditorialCandidateOptOut: (id, patch) => client.patch(`/editorial/candidates/${id}/opt-out`, patch).then((r) => r.data),
  // Etapa 6 — System Health
  adminSystemHealth: (refresh = false) => client.get("/system/health", { params: { refresh } }).then((r) => r.data),
  adminSystemHealthRefresh: () => client.post("/system/health/refresh").then((r) => r.data),
  adminSystemHealthAiTest: () => client.post("/system/health/ai/test").then((r) => r.data),
  adminSystemIncidents: (params = {}) => client.get("/system/incidents", { params }).then((r) => r.data),
  adminSystemIncidentDetail: (id) => client.get(`/system/incidents/${id}`).then((r) => r.data),
  adminSystemIncidentAck: (id) => client.post(`/system/incidents/${id}/acknowledge`).then((r) => r.data),
  adminSystemIncidentResolve: (id, note = "") => client.post(`/system/incidents/${id}/resolve`, { note }).then((r) => r.data),
  adminSystemActivity: (limit = 30) => client.get("/system/activity", { params: { limit } }).then((r) => r.data),

  // Social (Meta) — Phase 1: settings + connection tests
  adminSocialStatus: () => client.get("/admin/social/status").then((r) => r.data),
  adminSocialInstagramTest: () => client.post("/admin/social/instagram/test", null, { timeout: 30000 }).then((r) => r.data),
  adminSocialFacebookTest: () => client.post("/admin/social/facebook/test", null, { timeout: 30000 }).then((r) => r.data),
  adminSocialInstagramLimit: () => client.get("/admin/social/instagram/publishing-limit").then((r) => r.data),


  // Automation (Phase 1 — scaffolding, no scraping/AI running yet)
  adminAutoListSources: (params = {}) => client.get("/admin/automation/sources", { params }).then((r) => r.data),
  adminAutoCreateSource: (data) => client.post("/admin/automation/sources", data).then((r) => r.data),
  adminAutoUpdateSource: (id, patch) => client.patch(`/admin/automation/sources/${id}`, patch).then((r) => r.data),
  adminAutoDeleteSource: (id, force = false) => client.delete(`/admin/automation/sources/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),
  adminAutoListImported: (params = {}) => client.get("/admin/automation/imported", { params }).then((r) => r.data),
  adminAutoImportedCounts: () => client.get("/admin/automation/imported/counts").then((r) => r.data),
  adminAutoGetImported: (id) => client.get(`/admin/automation/imported/${id}`).then((r) => r.data),
  adminAutoUpdateImported: (id, patch) => client.patch(`/admin/automation/imported/${id}`, patch).then((r) => r.data),
  adminAutoListRuns: (params = {}) => client.get("/admin/automation/runs", { params }).then((r) => r.data),
  adminAutoListLogs: (params = {}) => client.get("/admin/automation/logs", { params }).then((r) => r.data),
  adminAutoGetSettings: () => client.get("/admin/automation/settings").then((r) => r.data),
  adminAutoPutSettings: (data) => client.put("/admin/automation/settings", data).then((r) => r.data),
  adminAutoTestSource: (id) => client.post(`/admin/automation/sources/${id}/test`).then((r) => r.data),
  adminAutoRunSource: (id) => client.post(`/admin/automation/sources/${id}/run`).then((r) => r.data),
  adminAutoRunAll: () => client.post("/admin/automation/run").then((r) => r.data),
  adminAutoRecoverSource: (id) => client.post(`/admin/automation/sources/${id}/recover`).then((r) => r.data),
  adminSocialImagesGenerate: (body) => client.post("/admin/social-images/generate", body).then((r) => r.data),
  adminSocialImagesList: (params) => client.get("/admin/social-images", { params }).then((r) => r.data),
  adminSocialImageDelete: (id) => client.delete(`/admin/social-images/${id}`).then((r) => r.data),
  adminSocialShortenTitle: (body) => client.post("/admin/social-images/shorten-title", body).then((r) => r.data),
  adminSocialImagesBatchGenerate: (body) => client.post("/admin/social-images/batch-generate", body).then((r) => r.data),
  adminSocialImagesJobStatus: (jobId) => client.get(`/admin/social-images/jobs/${jobId}`).then((r) => r.data),
  adminSocialImagesJobsList: (params) => client.get("/admin/social-images/jobs", { params }).then((r) => r.data),
  adminSocialImagesPreview: (body) => client.post("/admin/social-images/preview", body, { responseType: "blob" }).then((r) => r.data),
  adminSocialPresetsList: () => client.get("/admin/social-layout-presets").then((r) => r.data),
  adminSocialPresetSave: (body) => client.post("/admin/social-layout-presets", body).then((r) => r.data),
  adminSocialPresetDelete: (id) => client.delete(`/admin/social-layout-presets/${id}`).then((r) => r.data),
  adminSocialPresetDefault: () => client.get("/admin/social-layout-presets/default").then((r) => r.data),
  adminSocialPresetSetDefault: (id) => client.post(`/admin/social-layout-presets/${id}/default`).then((r) => r.data),
  adminSocialPresetClearDefault: () => client.post("/admin/social-layout-presets/clear-default").then((r) => r.data),
  adminSocialImageShare: (imageId) => client.post(`/admin/social-images/${imageId}/share`).then((r) => r.data),
  adminSocialImageDownload: (id) => client.get(`/admin/social-images/${id}/download`, { responseType: "blob" }).then((r) => r),
  adminSocialImagesDownloadBundle: (imageIds) =>
    client.post("/admin/social-images/download-bundle", { image_ids: imageIds }, { responseType: "blob" }).then((r) => r),
  adminSocialPublish: (body) => client.post("/admin/social/publish", body, { timeout: 90000 }).then((r) => r.data),
  adminSocialPublications: (params = {}) => client.get("/admin/social/publications", { params }).then((r) => r.data),
  adminSocialBatchPublish: (body) => client.post("/admin/social/batch-publish", body).then((r) => r.data),
  adminSocialBatchStatus: (batchId) => client.get(`/admin/social/batch/${batchId}`).then((r) => r.data),
  adminSocialBatchRetryFailed: (batchId) => client.post(`/admin/social/batch/${batchId}/retry-failed`).then((r) => r.data),
  adminSocialBatchesList: (params = {}) => client.get("/admin/social/batches", { params }).then((r) => r.data),
  adminSocialAutoPublishGet: () => client.get("/admin/social/auto-publish/settings").then((r) => r.data),
  adminSocialAutoPublishSave: (body) => client.put("/admin/social/auto-publish/settings", body).then((r) => r.data),
  adminSocialAutoPublishLog: (params = {}) => client.get("/admin/social/auto-publish/log", { params }).then((r) => r.data),
  adminSocialAutoPublishRunNow: (articleId) => client.post(`/admin/social/auto-publish/run-now/${articleId}`, null, { timeout: 90000 }).then((r) => r.data),
  adminDetectLocation: (body) => client.post("/admin/detect-location", body).then((r) => r.data),
  adminAutoScheduler: () => client.get("/admin/automation/scheduler").then((r) => r.data),
  adminAutoDashboard: (params = {}) => client.get("/admin/automation/dashboard", { params }).then((r) => r.data),
  adminAutoProcessAi: (id) => client.post(`/admin/automation/imported/${id}/process`).then((r) => r.data),
  adminAutoDeleteImported: (id) => client.delete(`/admin/automation/imported/${id}`).then((r) => r.data),
  adminAutoBatchDelete: (payload = {}) => client.post(`/admin/automation/imported/batch-delete`, payload).then((r) => r.data),
  adminAutoBatchProcess: (payload = {}) => client.post(`/admin/automation/imported/batch-process`, payload, { timeout: 600000 }).then((r) => r.data),
  adminAutoBatchProcessAndPublish: (payload = {}) => client.post(`/admin/automation/imported/batch-process-and-publish`, payload, { timeout: 600000 }).then((r) => r.data),
  adminAutoTestSourceMapping: (sourceId, payload) =>
    client.post(`/admin/automation/sources/${sourceId}/test-mapping`, payload).then((r) => r.data),
  adminAutoPreview: (id) => client.get(`/admin/automation/imported/${id}/preview`).then((r) => r.data),
  adminAutoSchedule: (id, scheduled_at) =>
    client.post(`/admin/automation/imported/${id}/schedule`, { scheduled_at }).then((r) => r.data),
  adminAutoPublish: (id, payload = { publish: true }) => client.post(`/admin/automation/imported/${id}/publish`, payload).then((r) => r.data),
  adminAutoAiConfigGet: () => client.get("/admin/automation/ai/config").then((r) => r.data),
  adminAutoAiConfigPut: (data) => client.put("/admin/automation/ai/config", data).then((r) => r.data),
  adminAutoAiConfigTest: () => client.post("/admin/automation/ai/test").then((r) => r.data),
  adminAiBudgetGet: () => client.get("/admin/ai/budget").then((r) => r.data),
  adminAiBudgetPut: (data) => client.put("/admin/ai/budget", data).then((r) => r.data),

  // Media library
  adminMediaStatus: () => client.get("/admin/media/status").then((r) => r.data),
  adminListMedia: (params = {}) => client.get("/admin/media", { params }).then((r) => r.data),
  adminGetMedia: (id) => client.get(`/admin/media/${id}`).then((r) => r.data),
  adminMediaUsage: (id) => client.get(`/admin/media/${id}/usage`).then((r) => r.data),
  adminUpdateMedia: (id, payload) => client.put(`/admin/media/${id}`, payload).then((r) => r.data),
  adminDeleteMedia: (id, force = false) => client.delete(`/admin/media/${id}`, { params: force ? { force: true } : {} }).then((r) => r.data),
  adminUploadMedia: (file, meta = {}, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    Object.entries(meta || {}).forEach(([k, v]) => {
      if (v != null) form.append(k, v);
    });
    return client
      .post("/admin/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
        timeout: 120000,
      })
      .then((r) => r.data);
  },
};

export default api;
